import { defineStore } from 'pinia';
import moment from 'moment';

import { useDestinationStore } from './destinationStore';
import { usePeriodStore } from './periodStore';
import { usePlanStore } from './planStore';
import { useShipmentStore } from './shipmentStore';
import { useDiscountStore } from './discountStore';
import { useDeviceStore } from './deviceStore';

export const useOrderStore = defineStore('orderStore', {
    state: () => {
        return {
            order_id: '',
            user_id: '',
            user: {
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
            },
            insurance_status: false,
            insurance_amount: 2,
        }
    },
    persist: {
        pick: ['numberOfDevices', 'user', 'insurance_status']
    },
    getters: {
        insurancePrice(state) {
            if (!state.insurance_status) return 0;

            const periodStore = usePeriodStore();
            const deviceStore = useDeviceStore();

            const days = periodStore.getMinMaxPeriodDays();
            const deviceQuantity = deviceStore.device_quantity;

            return days * state.insurance_amount * deviceQuantity;
        },
        subtotalAmount(state) {
            const destinationStore = useDestinationStore();
            const periodStore = usePeriodStore();
            const planStore = usePlanStore();
            const deviceStore = useDeviceStore();

            const destinations = destinationStore.selectedDestinations;
            const periods = periodStore.periods;
            const plans = planStore.plans;

            let amount = 0;
            destinations.forEach((destination, i) => {
                let plan = plans[i];
                let period = periods[i];
                
                if (destination && plan.price && period && period.startDate && period.endDate) {
                    let days = periodStore.getPeriodDays(i);

                    for (let i = 1; i <= days; i++) {
                        switch (true) {
                            case destination.api_country_id === 198:
                                if (i <= 10) {
                                    amount += plan.price;
                                } else if (i <= 20) {
                                    amount += 7;
                                } else {
                                    amount += 2.5;
                                }
                                break;
                            case destination.api_country_id === 180:
                                if (i <= 10) {
                                    amount += plan.price;
                                } else {
                                    amount += 7;
                                }
                                break;
                            default:
                                if (i <= 10) {
                                    amount += plan.price;
                                } else {
                                    amount += plan.price - 3;
                                }
                        }
                    }
                }
            });

            amount = amount * deviceStore.device_quantity;
            return Math.round(amount * 100) / 100;
        },
        totalAmount(state) {
            const shipmentStore = useShipmentStore();
            const discountStore = useDiscountStore();
            const deviceStore = useDeviceStore();

            const deliveryPrice = shipmentStore.deliveryPrice;
            const insurancePrice = this.insurancePrice;
            const discountPrice = discountStore.couponDiscountAmount;
            const giftCardDiscount = discountStore.giftCard ? discountStore.giftCard.amount : 0;
            const deviceExtraCharge = deviceStore.totalExtraCharge;
            const subtotal = this.subtotalAmount;

            let totalAmount = subtotal + insurancePrice + deliveryPrice + parseFloat(deviceExtraCharge);
            totalAmount = totalAmount - discountPrice - giftCardDiscount;
            totalAmount = totalAmount < 0 ? 0 : totalAmount;

            return totalAmount;
        },
        orderForm(state) {
            const destinationStore = useDestinationStore();
            const periodStore = usePeriodStore();
            const planStore = usePlanStore();
            const deviceStore = useDeviceStore();
            const shipmentStore = useShipmentStore();
            const discountStore = useDiscountStore();

            const data = {
                "destination_details": []
            };

            data['is_multi_destination'] = destinationStore.isMultiDestinationEnabled;

            data['order_id'] = state.order_id;
            data['user_id'] = state.user_id;
            data['user_first_name'] = state.user.first_name;
            data['user_last_name'] = state.user.last_name;
            data['user_email'] = state.user.email;
            data['user_phone_number'] = state.user.phone_number;
            data['user_address'] = shipmentStore.address;
            data['user_city'] = shipmentStore.city;
            data['user_country'] = shipmentStore.country;
            data['user_state'] = shipmentStore.state;
            data['user_zip_code'] = shipmentStore.zip_code;
            data['plan_type'] = planStore.plan_type;

            destinationStore.selectedDestinations.forEach((destination, i) => {
                if (data['plan_type'] === 'month') {
                    // Adjust start and end dates for month plans
                    const startDate = moment(periodStore.periods[i].startDate);
                    const endDate = moment(startDate).add(periodStore.monthCount[i], 'months').subtract(1, 'days');
                    periodStore.periods[i].startDate = startDate.format();
                    periodStore.periods[i].endDate = endDate.format();
                }
                data.destination_details.push({
                    "continent_id": destination.continent_id,
                    "continent_api_id": destination.continent.api_continent_id,
                    "country_id": destination.id,
                    "country_api_id": destination.api_country_id,
                    "country_name": destination.name,
                    "start_date": periodStore.periods[i].startDate,
                    "end_date": periodStore.periods[i].endDate,
                    "days": periodStore.getPeriodDays(i),
                    "plan_id": planStore.plans[i].id,
                    "plan_name": planStore.plans[i].name,
                    "plan_price": planStore.plans[i].price,
                });
            });

            data['device_id'] = deviceStore.selectedDevice.id;
            data['device_type'] = deviceStore.selectedDevice.type;
            data['device_name'] = deviceStore.selectedDevice.name;
            data['device_quantity'] = deviceStore.device_quantity;
            data['device_total_charge'] = deviceStore.totalExtraCharge;
            
            data['shipping_type'] = shipmentStore.shipment_type;
            data['pickup_location_id'] = shipmentStore.selectedPickupLocation ? shipmentStore.selectedPickupLocation.id : null;
            data['pickup_location_api_id'] = shipmentStore.selectedPickupLocation ? shipmentStore.selectedPickupLocation.api_pickup_location_id : null;
            data['pickup_location_name'] = shipmentStore.selectedPickupLocation ? shipmentStore.selectedPickupLocation.name : null;
            data['pickup_location_date'] = shipmentStore.pickup_date;
            data['delivery_type_id'] = shipmentStore.selectedDeliveryType ? shipmentStore.selectedDeliveryType.id : null;
            data['delivery_type_api_id'] = shipmentStore.selectedDeliveryType ? shipmentStore.selectedDeliveryType.api_shipping_type_id : null;
            data['delivery_type_name'] = shipmentStore.selectedDeliveryType ? shipmentStore.selectedDeliveryType.name : null;
            data['delivery_type_total_charge'] = shipmentStore.deliveryPrice;
            
            data['coupon_code'] = discountStore.couponCode;
            data['coupon_id'] = discountStore.coupon ? discountStore.coupon.id : null;
            data['coupon_total_amount'] = discountStore.couponDiscountAmount;

            data['gift_card_code'] = discountStore.giftCardCode;
            data['gift_card_id'] = discountStore.giftCard ? discountStore.giftCard.id : null;
            data['gift_card_total_amount'] = discountStore.giftCard ? discountStore.giftCard.amount : 0;
            
            data['insurance_status'] = state.insurance_status;
            data['insurance_total_charge'] = state.insurancePrice;

            data['plan_amount'] = this.subtotalAmount;
            data['total_amount'] = this.totalAmount;

            const utm = JSON.parse(localStorage.getItem('utm') || '{}')
            data['utm_source'] = utm.source;
            data['utm_medium'] = utm.medium;
            data['utm_campaign'] = utm.campaign;
            data['utm_content'] = utm.content;

            return data;
        }
    },
    actions: {
        setNumberOfDevices(payload) {
            this.numberOfDevices = payload.numberOfDevices;
        },
        setNumberOfDevicesByIndex(payload) {
            this.numberOfDevices[payload.index] = payload.value;
        },
        pushNumberOfDevices(payload) {
            this.numberOfDevices.push(payload.value);
        },
        popNumberOfDevices() {
            if (this.numberOfDevices.length > 1) {
                this.numberOfDevices.pop();
            }
        },
    }
});